import { clsx } from 'clsx';
import { memo } from 'react';
import { FormattedMessage } from 'react-intl';
import { HStack, VStack } from '@library';
import FlexLayout from '../../../core/components/layout/FlexLayout';
import RestrictedContent from '../../../core/components/router/RestrictedContent';
import useProductView from '../../hooks/useProductView';
import type { ProductWithRelatedData } from '../../types';
import ProductTileCarousel from '../ProductTileView/ProductTileCarousel';
import withErrorBoundary from '../../../core/components/errors/withErrorBoundary';
import SaleTag from '../Sale/SaleTag';
import ProductListStockListTable from './ProductListStockListTable';
import StocklistViewDescription from './ProductStocklistViewDescription';
import styles from './ProductStocklistView.module.scss';

interface ProductStocklistViewProps {
    product: ProductWithRelatedData;
    renderInstantly?: boolean;
    loading?: boolean;
}

const ProductStocklistView = memo(
    ({
        product,
        loading = false,
        renderInstantly = false,
        ...ariaProps
    }: ProductStocklistViewProps) => {
        const { isShow, ref, productDetailsLink } = useProductView({
            product,
            renderInstantly,
        });

        return (
            <RestrictedContent loggedInOnly>
                <div
                    className={clsx(styles.view, {
                        [styles.disableInteractions]: loading,
                    })}
                    ref={ref}
                    {...ariaProps}
                >
                    {isShow && (
                        <FlexLayout wrap="noWrap">
                            <div className={styles.description}>
                                <FlexLayout
                                    direction="column"
                                    gap="small"
                                    fullHeight
                                >
                                    <VStack className={styles.text} gap="mini">
                                        <StocklistViewDescription
                                            product={product}
                                            productDetailsLink={
                                                productDetailsLink
                                            }
                                        />
                                        {product.onSale && (
                                            <HStack justify="center">
                                                <SaleTag mini />
                                            </HStack>
                                        )}
                                    </VStack>
                                    <div>
                                        <ProductTileCarousel
                                            productDetailsLink={
                                                productDetailsLink
                                            }
                                            media={product.media.slice(0, 1)}
                                            paddingForSingle
                                        />
                                    </div>
                                </FlexLayout>
                            </div>
                            <div className={styles.stocklist}>
                                <ProductListStockListTable product={product} />
                            </div>
                        </FlexLayout>
                    )}
                </div>
            </RestrictedContent>
        );
    }
);

export default memo(
    withErrorBoundary(ProductStocklistView, {
        customMessage: <FormattedMessage id="Product can't be displayed" />,
    })
);
